import { QueryBuilder } from 'utils/elasticsearch/query_builder';
import _search from 'utils/ReportingWebAPIUtils';
import ROLES from 'constants/UserRoles';
import { getCurrencySymbol } from 'components/modules/master/formatters';
import accounting from 'accounting';

function normalizeJob(raw) {
  const job = raw._source;
  return {
    _id: job._id,
    shipment_id: job.shipmentId,
    bundle_id: job.bundleId,
    mg_load_id: job.mgLoadId,
    sequence_number: job.sequenceNumber,
    tracking_id: job.shipmentReferenceId,
    shipper_id: job.shipperId,
    shipper_name: job.shipperName,
    carrier_name: job.carrierName,
    driver_name: job.driverName,
    pickup_after: job.pickupAfterDate,
    pickup_by: job.pickupByDate,
    deliver_after: job.deliverAfterDate,
    deliver_by: job.deliverByDate,
    origin: {
      name: job.originName,
      street: job.originStreet,
      city: job.originCity,
      state: job.originState,
      zipcode: job.originZip,
      address: job.originAddress,
      timezone: job.originTimezone,
    },
    destination: {
      name: job.destinationName,
      street: job.destinationStreet,
      city: job.destinationCity,
      state: job.destinationState,
      zipcode: job.destinationZip,
      address: job.destinationAddress,
      timezone: job.destinationTimezone,
    },
    mo_number: job.moNumber,
    house_bill_number: job.houseBillNumber,
    tags: job.tags,
    latestStatus: job.latestStatus,
    shipmentStatus: job.shipmentStatus,
    weight: job.weight,
    pallets: job.numberOfPallets,
    boxes: job.numberOfBoxes,
    carrier_total: accounting.formatMoney(
      Math.round(job.carrierTotal * 10) / 10,
      getCurrencySymbol(DEFAULT_CURRENCY),
    ),
    shipper_total: accounting.formatMoney(
      Math.round(job.shipperTotal * 10) / 10,
      getCurrencySymbol(DEFAULT_CURRENCY),
    ),
    attempt_type: job.attemptType,
    attempt_reason: job.attemptReason,
  };
}

function buildSearchQuery(query, pageNum, limit = 20) {
  const builder = new QueryBuilder();

  builder
    .size(limit)
    .filterNot({ system: 'dray' })
    .from(limit * (pageNum - 1));

  if (query.sort.key === 'latestStatus.code') {
    builder.sort(query.sort.key, query.sort.order, 'max');
  } else {
    builder.sort(query.sort.key, query.sort.order);
  }

  if (query.markets.length) {
    builder.filterOr(query.markets.map((market) => ({ market })));
  }

  if (query.keywords.length) {
    builder.match(query.keywords);
  }

  query.dateRanges.forEach((obj) => {
    const key = {
      pickup: 'pickupByDate',
      deliver: 'deliverByDate',
    }[obj.key];
    builder.range(key, obj.start, obj.end);
  });

  if (query.tags.length) {
    builder.filter({ tags: query.tags });
  }

  if (query.shipmentStatuses.length) {
    builder.filter({ 'latestStatus.slug': query.shipmentStatuses });
  }

  if (query.excludeAcceptedOffers) {
    builder.filter({ accepted: false });
  }

  if (query.unbundled) {
    builder.filterMissing('bundleId');
  }

  if (query.bundleId) {
    builder.raw({
      or: [
        { term: { bundleId: query.bundleId } },
        { missing: { field: 'bundleId' } },
      ],
    });
  }

  if (query.shipmentTypes.length) {
    builder.filter({ shipmentType: query.shipmentTypes });
  }

  if (query.accessorials.length) {
    builder.filter({ 'lineItems.category': query.accessorials });
  }

  if (query.shipperId) {
    builder.filter({ shipperId: query.shipperId });
  }

  if (query.carrierId) {
    builder.filter({ carrierId: query.carrierId });
  }

  if (query.driverId) {
    builder.filter({ driverId: query.driverId });
  }

  if (query.user) {
    if (query.user._type === ROLES.SHIPPER) {
      builder.filter({ shipperId: query.user._id });
    }
  }

  if (query.geo.origin || query.geo.destination) {
    builder.filterGeo(query.geo);
  }

  if (query.current) {
    builder.filter({ closed: false, canceled: false });
  }

  if (query.project) builder.filter({ project: query.project });

  if (query.accepted !== undefined) {
    builder.filter({ accepted: query.accepted });
  }

  return builder.body;
}

function search(query, pageNum, limit) {
  const builderQuery = buildSearchQuery(query, pageNum, limit);

  return _search(builderQuery).then((result) => ({
    count: result.hits.total,
    data: result.hits.hits.map(normalizeJob),
  }));
}

function distincts(fields = [], limit = 100) {
  const filter = {
    bool: {
      must_not: [{ term: { system: 'dray' } }],
    },
  };
  const aggs = fields.reduce((a, field) => {
    const b = a;
    b[`distinct_${field}`] = {
      terms: {
        field,
        size: limit,
      },
    };
    return b;
  }, {});

  const body = {
    filter,
    aggs,
    size: 0,
  };
  return _search(body, 'job').then((results) =>
    fields.reduce((a, field) => {
      const b = a;
      b[field] = results.aggregations[`distinct_${field}`].buckets.map(
        (raw) => raw.key,
      );
      return b;
    }, {}),
  );
}

export default {
  buildSearchQuery,
  search,
  distincts,
};
